import type { PaddockUsageEntry } from '@/types/apiModels'

export type PaddockPair = {
  baseline: PaddockUsageEntry | undefined
  project: PaddockUsageEntry | undefined
}

/**
 * Pairs baseline and project paddocks using the `predecessorPaddockIds` relation
 * on project-year entries. Ordering:
 *
 * 1. Rows anchored to a baseline paddock — sorted by baseline name, then by
 *    project name within the group (covers 1:1, 1:N, and N:1 merges).
 * 2. Orphan project paddocks (no predecessorPaddockIds match) — sorted by
 *    project paddock name, appended after all paired rows.
 *
 * A baseline paddock with no successor produces one row with `project: undefined`.
 * A project paddock with no matching predecessor produces one row with `baseline: undefined`.
 * A project paddock with multiple predecessors produces one row per predecessor.
 */
export function pairPaddocksByPredecessor(
  baselinePaddocks: PaddockUsageEntry[],
  projectPaddocks: PaddockUsageEntry[],
): PaddockPair[] {
  const baselineById = new Map(baselinePaddocks.map(p => [p.paddockId, p]))

  // Build a map: baselineId → project paddocks that reference it, and track paired project IDs.
  const successorsByBaselineId = new Map<string, PaddockUsageEntry[]>()
  const pairedProjectIds = new Set<string>()

  projectPaddocks.forEach(projectPaddock => {
    ;(projectPaddock.predecessorPaddockIds ?? []).forEach(predecessorId => {
      if (baselineById.has(predecessorId)) {
        const successors = successorsByBaselineId.get(predecessorId) ?? []
        successors.push(projectPaddock)
        successorsByBaselineId.set(predecessorId, successors)
        pairedProjectIds.add(projectPaddock.paddockId)
      }
    })
  })

  // Sort baselines by name for stable output.
  const sortedBaseline = [...baselinePaddocks].sort((a, b) =>
    a.paddockName.localeCompare(b.paddockName),
  )

  const pairedRows: PaddockPair[] = sortedBaseline.flatMap(baselinePaddock => {
    const successors = successorsByBaselineId.get(baselinePaddock.paddockId)

    if (!successors || successors.length === 0) {
      return [{ baseline: baselinePaddock, project: undefined }]
    }

    return [...successors]
      .sort((a, b) => a.paddockName.localeCompare(b.paddockName))
      .map(projectPaddock => ({ baseline: baselinePaddock, project: projectPaddock }))
  })

  // Orphan project paddocks (predecessorPaddockIds is empty or references unknown baselines).
  const orphanRows: PaddockPair[] = projectPaddocks
    .filter(p => !pairedProjectIds.has(p.paddockId))
    .sort((a, b) => a.paddockName.localeCompare(b.paddockName))
    .map(p => ({ baseline: undefined, project: p }))

  return [...pairedRows, ...orphanRows]
}
